import { useState } from 'react';
import { X } from 'lucide-react';

const SAMPLE_SEPARATOR = `Checkout should be fast and secure for all users
---
Implement rate limiting at 100 req/min per API key with HTTP 429 response
---
Employee onboarding must be completed promptly with proper documentation`;

const SAMPLE_NUMBERED = `1. Checkout should be fast and secure for all users
2. Implement rate limiting at 100 req/min per API key with HTTP 429 response
3. Employee onboarding must be completed promptly with proper documentation`;

const countBlocks = (raw) => {
  if (!raw.trim()) return 0;
  if (/^-{3,}\s*$/m.test(raw)) {
    return raw.split(/^-{3,}\s*$/m).map(b => b.trim()).filter(b => b.length > 0).length;
  }
  const lines = raw.split('\n');
  const numberedCount = lines.filter(l => /^\d+[.)]\s/.test(l)).length;
  return numberedCount > 0 ? numberedCount : (raw.trim() ? 1 : 0);
};

export default function BatchScanModal({ onClose, onBatchScan }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sampleFormat, setSampleFormat] = useState('separator');

  const blockCount = countBlocks(text);

  const detectedFormat = !text.trim() ? null
    : /^-{3,}\s*$/m.test(text) ? 'separator'
    : text.split('\n').some(l => /^\d+[.)]\s/.test(l)) ? 'numbered'
    : null;

  const handleScan = async () => {
    if (!text.trim()) return;
    setLoading(true);
    onClose();
    await onBatchScan(text);
    setLoading(false);
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => e.target === e.currentTarget && onClose()}
      data-testid="batch-scan-modal-backdrop"
    >
      <div style={{ background: '#141419', border: '1px solid #1f1f2e', borderRadius: 14, width: '100%', maxWidth: 620, padding: 28 }} data-testid="batch-scan-modal">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ color: '#f4f4f5', fontSize: 18, fontWeight: 700, margin: 0 }}>Batch Scan</h2>
            <p style={{ color: '#52525b', fontSize: 13, margin: '4px 0 0' }}>
              Separate requirements with <code style={{ background: '#0d0d12', color: '#818cf8', padding: '1px 5px', borderRadius: 4, fontSize: 12 }}>---</code> lines or use a numbered list.
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', padding: 4 }} data-testid="batch-modal-close">
            <X size={20} />
          </button>
        </div>

        <div style={{ background: '#0d0d12', border: '1px solid #1f1f2e', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ color: '#3f3f46', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Expected Format</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[{ id: 'separator', label: '--- lines' }, { id: 'numbered', label: '1. / 1)' }].map(f => (
                <button
                  key={f.id}
                  onClick={() => setSampleFormat(f.id)}
                  style={{
                    background: sampleFormat === f.id ? '#1f1f2e' : 'transparent',
                    border: `1px solid ${sampleFormat === f.id ? '#2a2a3a' : 'transparent'}`,
                    borderRadius: 5, padding: '2px 8px', color: sampleFormat === f.id ? '#a1a1aa' : '#52525b',
                    fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <pre style={{ color: '#52525b', fontSize: 12, fontFamily: 'JetBrains Mono, monospace', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
            {sampleFormat === 'separator' ? SAMPLE_SEPARATOR : SAMPLE_NUMBERED}
          </pre>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <label style={{ color: '#a1a1aa', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Your Requirements</label>
            {blockCount > 0 && (
              <span style={{ color: '#6366f1', fontSize: 12, fontWeight: 600 }} data-testid="block-count-label">
                {blockCount} block{blockCount !== 1 ? 's' : ''} detected
                {detectedFormat && <span style={{ color: '#3f3f46', fontWeight: 400, marginLeft: 5 }}>({detectedFormat === 'separator' ? '--- format' : 'numbered'})</span>}
              </span>
            )}
          </div>
          <textarea
            data-testid="batch-textarea"
            value={text}
            onChange={e => setText(e.target.value)}
            rows={11}
            placeholder={SAMPLE_SEPARATOR}
            style={{
              width: '100%', background: '#0d0d12', border: '1px solid #2a2a3a',
              borderRadius: 8, padding: '12px 14px', color: '#f4f4f5',
              fontSize: 13, lineHeight: 1.7, outline: 'none', resize: 'vertical', fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            data-testid="batch-scan-all-btn"
            onClick={handleScan}
            disabled={blockCount === 0 || loading}
            style={{
              flex: 1, background: '#6366f1', border: 'none', borderRadius: 8,
              padding: '10px 20px', color: '#fff', fontSize: 14, fontWeight: 600,
              cursor: blockCount === 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              opacity: blockCount === 0 ? 0.45 : 1,
              boxShadow: blockCount > 0 ? '0 0 16px rgba(99,102,241,0.3)' : 'none',
            }}
          >
            {loading ? 'Launching...' : `Scan All (${blockCount} ticket${blockCount !== 1 ? 's' : ''})`}
          </button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }} data-testid="batch-modal-cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
